// Where each answer lands on the template.
//
// The piece that makes Smart PDF work on a REAL customer document: the ones
// people actually hand over are flat designs with no named fields, so there is
// nothing to map and the only way to say "the company name goes here" is to
// point at the page.
//
// R5 (docs/PLAYBOOK-redesign.md §7): this used to be a full-screen editor of
// its own over the Document tab, with a field rail, the page and a properties
// rail. The tab is now laid out that way itself — the Outline picks, the page
// sits in the middle, the inspector holds the properties — so what is left
// here is the two pieces the tab embeds: `PlacementCanvas` (the pdf.js page
// with the boxes, the drags and the drops) and `PlacementProps` (one selected
// box's or table's properties). Nothing about the coordinates, the gestures
// or the stored shapes changed.
//
// ---------------------------------------------------------------------------
// Coordinates
// ---------------------------------------------------------------------------
// Two frames meet here and nowhere else, which is the entire reason this file
// exists as one component:
//
//   the screen   pixels, origin TOP-left, at whatever scale the page renders
//   the PDF      points, origin BOTTOM-left, at the page's own size
//
// Everything STORED is in PDF points. The conversion happens at this boundary,
// twice (`toPdf`, `toScreen`), and nothing downstream — not the sanitizer, not
// the stamper — knows a screen exists. Getting this wrong is the classic
// "everything is upside down" bug, and the way not to have it is for exactly
// one file to know both frames.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FieldPicker from './FieldPicker';
import type { OptGroup } from './fieldGroups';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type { FormPdfTemplate } from '../../api/forms';

import { loadPdfJs } from './pdfjs';

export interface Placement {
  fieldKey: string;
  page: number;
  x: number; y: number; w: number; h: number;
  fontSize?: number;
  align?: 'left' | 'center' | 'right';
  color?: string;
  format?: 'text' | 'date' | 'currency' | 'number' | 'checkbox' | 'signedAt';
  /** `signedAt` only: print the signer's address beside the moment. */
  withIp?: boolean;
  shrinkToFit?: boolean;
}

/**
 * A repeating table: one row per instance of a widget scope.
 *
 * `y` is the BASELINE ROW — the top row's box — and each row after it is drawn
 * `rowHeight` further down the page. Columns are offsets from the region's own
 * left edge, so moving the table moves its columns with it.
 */
export interface Region {
  scope: string;
  page: number;
  x: number; y: number; w: number;
  rowHeight: number;
  maxRows?: number;
  /**
   * Absent = a table. `blocks` is a repeating block: one box per item with
   * each answer placed anywhere inside it (`dy` down from the block's top, in
   * a box `h` tall it wraps within), the box repeated `rowHeight` further down
   * the page each time. See `PdfRegion` on the server.
   */
  layout?: 'blocks';
  /** Blocks: the colour painted over the slots a page has no item for. */
  cover?: string;
  columns: RegionColumn[];
  total?: { column: string; op: 'sum' | 'count'; dx: number; label?: string };
}

export interface RegionColumn {
  fieldKey: string; dx: number; w: number;
  align?: 'left' | 'center' | 'right';
  format?: 'text' | 'date' | 'currency' | 'number' | 'checkbox';
  /** Blocks only. */
  dy?: number; h?: number; fontSize?: number;
  /** Instead of an answer: how many of this nested container the item holds. */
  count?: string;
  /** Instead of an answer: the item's number — 1, 2, 3… */
  index?: boolean;
}

/** One repeatable container of the form, and the answers that repeat with it. */
export interface ScopeOption {
  scope: string;
  label: string;
  fields: { key: string; label: string }[];
  /** The containers nested inside this one, at any depth — what a block can count. */
  inside?: string[];
}

/** A new block's height, and how many a page starts out holding. */
export const BLOCK_H = 96;
const BLOCK_ROWS = 4;
/** One answer's box in a block: a single line of 10pt, with the server's leading. */
const CELL_H = 13;

/**
 * A table turned into a repeating block, its top where the table's was: the
 * columns keep their place across and become one-line boxes along the top,
 * to be moved where the block's design wants them.
 */
export function toBlocks(r: Region): Region {
  const { total: _total, ...rest } = r;
  const rowHeight = Math.max(r.rowHeight, BLOCK_H);
  return {
    ...rest, layout: 'blocks', rowHeight,
    y: r.y + r.rowHeight - rowHeight,
    maxRows: Math.min(r.maxRows || BLOCK_ROWS, BLOCK_ROWS),
    columns: r.columns.map((c) => ({ ...c, dy: c.dy ?? 0, h: c.h ?? CELL_H, fontSize: c.fontSize ?? 10 })),
  };
}

/** …and back: a line per item, with only what a table's column has. */
export function toTable(r: Region): Region {
  const { layout: _layout, cover: _cover, ...rest } = r;
  return {
    ...rest, rowHeight: ROW_H,
    y: r.y + r.rowHeight - ROW_H,
    columns: r.columns.filter((c) => !c.count && !c.index)
      .map(({ dy: _dy, h: _h, fontSize: _size, ...c }) => c),
  };
}

/** What the inspector is editing: ONE selection across the two kinds of thing. */
export type PlacementSel = { kind: 'placement' | 'region'; index: number } | null;
/** A chip a tap armed: the next tap on the page drops it (§4E E3). */
export type Armed = { kind: 'field' | 'scope'; key: string } | null;

/** Where a new box starts, in points — a comfortable single line. */
export const NEW_W = 200;
export const NEW_H = 16;
/** A new table's row height: a printed line with a little air. */
export const ROW_H = 18;
/** A new signature box: tall enough for a mark to be legible. */
export const SIG_H = 40;

/**
 * A new box for `key`, its top-left corner at (`x`, `top`) measured from the
 * page's TOP in points — the one place a caller thinks in "from the top".
 * The stored `y` is the box's bottom in PDF space.
 */
export function newPlacement(key: string, page: number, pageHeight: number, x: number, top: number, signature: boolean): Placement {
  const h = signature ? SIG_H : NEW_H;
  return { fieldKey: key, page, x: Math.round(x), y: Math.round(pageHeight - top - h), w: NEW_W, h, fontSize: 10, align: 'left' };
}

/** A new table for `scope`, its first row's top at `top` from the page's top. */
export function newRegion(scope: string, page: number, pageHeight: number, x: number, top: number, firstColumn?: string): Region {
  return {
    scope, page,
    x: Math.round(x), y: Math.round(pageHeight - top - ROW_H),
    w: 400, rowHeight: ROW_H, maxRows: 8,
    // A table starts with one column, so it draws something immediately — an
    // empty table is invisible and reads as the drop having failed.
    columns: firstColumn ? [{ fieldKey: firstColumn, dx: 0, w: 180 }] : [],
  };
}

export interface PlacementCanvasProps {
  template: FormPdfTemplate;
  /** key → label, for the box labels */
  labelOf: (key: string) => string;
  scopes: ScopeOption[];
  signatureKeys: string[];
  placements: Placement[];
  regions: Region[];
  onChange: (placements: Placement[], regions: Region[]) => void;
  page: number;
  onPage: (page: number) => void;
  sel: PlacementSel;
  onSel: (sel: PlacementSel) => void;
  /** the chip a tap armed on a tablet; the next tap on the page drops it */
  armed: Armed;
  onArmed: (armed: Armed) => void;
  /** under 900: the page is fitted to the pane it has instead of the desktop's 820px */
  narrow: boolean;
  /** on a plan without `smartPdf` the boxes are shown and nothing moves */
  readOnly?: boolean;
  /** When the canvas is not the place to accept a drop (a stale render): the tab decides. */
  canDrop?: (kind: 'field' | 'scope', key: string) => boolean;
}

/**
 * The page image and everything on it: the boxes (pointer-dragged, resized by
 * their grip), the repeating tables (dragged, resized), a drop from the
 * Outline or from a chip, and the tap that lands an armed chip. The state —
 * which page, what is selected, the boxes themselves — is the tab's, so the
 * inspector beside the page sees the same thing the page does.
 */
export function PlacementCanvas({
  template, labelOf, scopes, signatureKeys, placements, regions, onChange, page, sel, onSel, armed, onArmed, narrow, readOnly, canDrop,
}: PlacementCanvasProps) {
  const isSignature = useCallback((key: string) => signatureKeys.includes(key), [signatureKeys]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** Rendered size of THIS page, and the scale it was rendered at. */
  const [view, setView] = useState({ w: 0, h: 0, scale: 1, pageH: template.height });
  const wrapRef = useRef<HTMLDivElement | null>(null);
  /**
   * How wide the page pane is, in whole hundreds of pixels.
   *
   * A BUCKET rather than the number, because this is a render dependency: the
   * exact width changes the moment a scrollbar appears, and re-running the
   * draw on that cancels the draw that caused it — which leaves the page
   * saying "Drawing the page…" for ever. A rotation crosses a bucket; a
   * scrollbar does not. The exact width is read inside the effect, where it
   * is only a value.
   */
  const [paneBucket, setPaneBucket] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !narrow || typeof ResizeObserver === 'undefined') return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const read = () => setPaneBucket(Math.round(el.clientWidth / 100));
    const later = () => { if (t) clearTimeout(t); t = setTimeout(read, 150); };
    read();
    const ro = new ResizeObserver(later);
    ro.observe(el);
    return () => { if (t) clearTimeout(t); ro.disconnect(); };
  }, [narrow]);

  const scopeOf = useCallback((scope: string) => scopes.find((s) => s.scope === scope), [scopes]);

  // ---- the page image -----------------------------------------------------
  //
  // Loading the DOCUMENT and drawing a PAGE are two effects on purpose. They
  // were one, and it was wrong: the document effect's cleanup destroys the
  // loading task, and with `page` in its dependencies that cleanup ran on
  // every page change — tearing down the document the next `getPage` needed,
  // which surfaced as "Cannot read properties of null (reading
  // 'sendWithPromise')" and a canvas stuck on page 1.

  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);

  useEffect(() => {
    let cancelled = false;
    let task: { promise: Promise<PDFDocumentProxy>; destroy: () => void } | null = null;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const pdfjs = await loadPdfJs();
        if (cancelled) return;
        task = pdfjs.getDocument({ url: template.url });
        const loaded = await task.promise;
        if (cancelled) return;
        setDoc(loaded);
      } catch (e) {
        if (!cancelled) {
          setLoading(false);
          setError((e as Error).message || 'That template could not be opened.');
        }
      }
    })();
    return () => { cancelled = true; task?.destroy(); };
  }, [template.url]);

  useEffect(() => {
    if (!doc) return undefined;
    let cancelled = false;
    /** The in-flight render, so a fast page change cancels rather than races. */
    let render: { cancel: () => void } | null = null;

    (async () => {
      setLoading(true);
      try {
        const pdfPage = await doc.getPage(page);
        if (cancelled) return;

        // Fit the width of the pane rather than a fixed zoom: a Letter page and
        // an A4 one should both fill it, and the scale is recorded so the
        // conversions below stay honest whatever it came out as.
        const base = pdfPage.getViewport({ scale: 1 });
        // 820 is the width the desktop has always drawn at, capped to the pane
        // the tab gives the page; under 900 the pane is narrower than that, so
        // the page is fitted to it instead — same cap, same floor, same
        // recorded scale.
        const paneW = wrapRef.current?.clientWidth || 0;
        const fit = paneW > 0 ? Math.min(820, paneW - 20) : 820;
        const scale = Math.min(1.6, Math.max(0.4, fit / base.width));
        const viewport = pdfPage.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const drawing = pdfPage.render({ canvas, canvasContext: ctx, viewport });
        render = drawing;
        await drawing.promise;
        if (cancelled) return;
        // Set AFTER the draw: the boxes are positioned from `view`, and
        // painting them against a page that is not on screen yet makes them
        // jump when it arrives.
        setView({ w: viewport.width, h: viewport.height, scale, pageH: base.height });
      } catch (e) {
        // A cancelled render is the expected outcome of clicking Next twice,
        // not something to put in front of anybody.
        const msg = (e as Error).message || '';
        if (!cancelled && !/cancel/i.test(msg)) setError(msg || 'That page could not be drawn.');
      } finally {
        // A run cancelled by the next one leaves `loading` for that one to
        // clear; a run cancelled by an unmount has no next one, and the flag
        // goes with the component.
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; render?.cancel(); };
  }, [doc, page, narrow, paneBucket]);

  // ---- the two conversions ------------------------------------------------

  /** Screen pixels (origin top-left) -> PDF points (origin bottom-left). */
  const toPdf = useCallback((px: number, py: number) => ({
    x: px / view.scale,
    y: view.pageH - py / view.scale,
  }), [view]);

  /** Where a box sits over the page image. `bottom` is its PDF y. */
  const boxStyle = useCallback((x: number, bottom: number, w: number, h: number) => ({
    left: x * view.scale,
    // The PDF y is the box's BOTTOM, so its top is y + h.
    top: (view.pageH - (bottom + h)) * view.scale,
    width: w * view.scale,
    height: h * view.scale,
  }), [view]);

  // ---- editing ------------------------------------------------------------

  const boxesOnPage = useMemo(
    () => placements.map((p, i) => ({ p, i })).filter((x) => x.p.page === page),
    [placements, page]
  );
  const regionsOnPage = useMemo(
    () => regions.map((r, i) => ({ r, i })).filter((x) => x.r.page === page),
    [regions, page]
  );

  const updateP = (index: number, patch: Partial<Placement>) =>
    onChange(placements.map((p, i) => (i === index ? { ...p, ...patch } : p)), regions);
  const updateR = (index: number, patch: Partial<Region>) =>
    onChange(placements, regions.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  /** Rows a region shows on screen — its ceiling, or a sample of three. */
  const rowsShown = (r: Region) => Math.max(1, Math.min(12, r.maxRows || 3));

  const dropAt = (kind: 'field' | 'scope', key: string, clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (canDrop && !canDrop(kind, key)) return;
    const { x, y } = toPdf(clientX - rect.left, clientY - rect.top);
    // `y` is where the pointer is; the box hangs below it, so its top is there.
    const top = view.pageH - y;
    if (kind === 'field') {
      onChange([...placements, newPlacement(key, page, view.pageH, x, top, isSignature(key))], regions);
      onSel({ kind: 'placement', index: placements.length });
      return;
    }
    onChange(placements, [...regions, newRegion(key, page, view.pageH, x, top, scopeOf(key)?.fields[0]?.key)]);
    onSel({ kind: 'region', index: regions.length });
  };

  /**
   * Drag something already on the page. Deltas are screen pixels; the store
   * stays in points. Pointer events, `touch-action: none` on the box (CSS)
   * and a `pointercancel` handler, so a finger can do it too.
   */
  const startMove = (kind: 'placement' | 'region', index: number, e: React.PointerEvent) => {
    e.stopPropagation();
    onSel({ kind, index });
    if (readOnly) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = kind === 'placement' ? placements[index] : regions[index];
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
    // The latest position, so the store is written from the last event and
    // never from a stale closure over the lists.
    let last: { x: number; y: number } | null = null;

    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / view.scale;
      // Screen y grows downward and PDF y grows upward: the sign flip here is
      // the whole of "why is it moving the wrong way" if it is ever dropped.
      const dy = -(ev.clientY - startY) / view.scale;
      last = { x: Math.round(origin.x + dx), y: Math.round(origin.y + dy) };
      if (kind === 'placement') updateP(index, last); else updateR(index, last);
    };
    const up = () => {
      try { target.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
      target.removeEventListener('pointercancel', up);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
    target.addEventListener('pointercancel', up);
  };

  /** The bottom-right handle of a placement: size only, never position. */
  const startResize = (index: number, e: React.PointerEvent) => {
    e.stopPropagation();
    onSel({ kind: 'placement', index });
    if (readOnly) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = placements[index];
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    const move = (ev: PointerEvent) => {
      const dw = (ev.clientX - startX) / view.scale;
      const dh = (ev.clientY - startY) / view.scale;
      // Growing downward moves the BOTTOM down, so y falls as h rises — the
      // box's top edge is what stays put, which is what dragging feels like.
      updateP(index, {
        w: Math.max(8, Math.round(origin.w + dw)),
        h: Math.max(8, Math.round(origin.h + dh)),
        y: Math.round(origin.y - dh),
      });
    };
    const up = () => {
      try { target.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
      target.removeEventListener('pointercancel', up);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
    target.addEventListener('pointercancel', up);
  };

  /** A region's handle changes its WIDTH and its row height, not its top. */
  const startResizeRegion = (index: number, e: React.PointerEvent) => {
    e.stopPropagation();
    onSel({ kind: 'region', index });
    if (readOnly) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = regions[index];
    const rows = rowsShown(origin);
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    const move = (ev: PointerEvent) => {
      const dw = (ev.clientX - startX) / view.scale;
      // The handle sits under the LAST visible row, so a drag of dh spreads
      // across every row rather than growing one of them by the whole amount.
      const dh = (ev.clientY - startY) / view.scale / rows;
      const rowHeight = Math.max(6, Math.round(origin.rowHeight + dh));
      updateR(index, {
        w: Math.max(40, Math.round(origin.w + dw)),
        rowHeight,
        // The first row's top stays where it was put.
        y: origin.y + origin.rowHeight - rowHeight,
      });
    };
    const up = () => {
      try { target.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
      target.removeEventListener('pointercancel', up);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
    target.addEventListener('pointercancel', up);
  };

  /** What a block's box says it prints. */
  const cellLabel = (c: RegionColumn) =>
    c.index ? '1, 2, 3…' : c.count ? `How many ${scopeOf(c.count)?.label || c.count}` : labelOf(c.fieldKey);

  /**
   * Move or size one answer's box inside a block. Its offsets are from the
   * block's top-left and BOTH grow the way the screen does — `dy` is "down
   * from the top of the block" — so, unlike a box on the page, nothing flips.
   */
  const startCell = (what: 'move' | 'size', index: number, col: number, e: React.PointerEvent) => {
    e.stopPropagation();
    onSel({ kind: 'region', index });
    if (readOnly) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const region = regions[index];
    const origin = region.columns[col];
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / view.scale;
      const dy = (ev.clientY - startY) / view.scale;
      const patch: Partial<RegionColumn> = what === 'move'
        ? { dx: Math.round(origin.dx + dx), dy: Math.round((origin.dy ?? 0) + dy) }
        : { w: Math.max(8, Math.round(origin.w + dx)), h: Math.max(6, Math.round((origin.h ?? CELL_H) + dy)) };
      updateR(index, { columns: region.columns.map((c, j) => (j === col ? { ...c, ...patch } : c)) });
    };
    const up = () => {
      try { target.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
      target.removeEventListener('pointercancel', up);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
    target.addEventListener('pointercancel', up);
  };

  return (
    <div className={`pe-canvas-wrap${readOnly ? ' is-readonly' : ''}`} ref={wrapRef}>
      {error && <div className="pe-error">{error}</div>}
      {armed && (
        <div className="pe-hint pe-armed-hint">
          Tap the page where <b>{armed.kind === 'field' ? labelOf(armed.key) : (scopeOf(armed.key)?.label || armed.key)}</b> goes.{' '}
          <button type="button" className="fs-btn sm" onClick={() => onArmed(null)}>Cancel</button>
        </div>
      )}
      <div
        className="pe-page"
        style={{ width: view.w || undefined, height: view.h || undefined }}
        onDragOver={readOnly ? undefined : (e) => e.preventDefault()}
        onDrop={readOnly ? undefined : (e) => {
          e.preventDefault();
          // Not while the page is still drawing: the frame on screen is
          // the previous page's, and a box dropped now would be placed
          // with that page's height.
          if (loading) return;
          const payload = e.dataTransfer.getData('text/plain');
          const cut = payload.indexOf(':');
          const kind = cut < 0 ? '' : payload.slice(0, cut);
          const key = cut < 0 ? '' : payload.slice(cut + 1);
          if (key && (kind === 'field' || kind === 'scope')) {
            dropAt(kind, key, e.clientX, e.clientY);
          }
        }}
        onPointerDown={() => onSel(null)}
        onClick={(e) => {
          // The tap half of the drag path (§4E E3): the same `dropAt`,
          // given the tap's own coordinates.
          if (!armed || loading || readOnly) return;
          dropAt(armed.kind, armed.key, e.clientX, e.clientY);
          onArmed(null);
        }}
      >
        <canvas ref={canvasRef} className="pe-canvas" />
        {loading && <div className="pe-loading">Drawing the page…</div>}

        {regionsOnPage.map(({ r, i }) => {
          const rows = rowsShown(r);
          const style = boxStyle(r.x, r.y - (rows - 1) * r.rowHeight, r.w, r.rowHeight * rows);
          const on = sel?.kind === 'region' && sel.index === i;
          return (
            <div
              key={`r${i}`}
              className={`pe-region ${on ? 'is-on' : ''}`}
              style={style}
              onPointerDown={(e) => startMove('region', i, e)}
              title={scopeOf(r.scope)?.label || r.scope}
            >
              <span className="pe-box-label">
                {r.layout === 'blocks'
                  ? <>{scopeOf(r.scope)?.label || r.scope} · block · {r.columns.length} answer{r.columns.length === 1 ? '' : 's'}</>
                  : <>{scopeOf(r.scope)?.label || r.scope} · {r.columns.length} column{r.columns.length === 1 ? '' : 's'}</>}
              </span>
              {/* Row guides, so the row height is something you can see
                  rather than a number you have to imagine. */}
              {Array.from({ length: rows - 1 }, (_, k) => (
                <span
                  key={k}
                  className="pe-region-rule"
                  style={{ top: `${((k + 1) / rows) * 100}%` }}
                />
              ))}
              {r.layout === 'blocks'
                // Each answer's box, in every block shown: the first block's
                // are the ones that move, the rest show where they repeat.
                ? Array.from({ length: rows }, (_, row) => r.columns.map((c, k) => (
                  <span
                    key={`${row}-${k}`}
                    className={`pe-region-cell${row ? ' is-echo' : ''}`}
                    style={{
                      left: c.dx * view.scale, top: (row * r.rowHeight + (c.dy ?? 0)) * view.scale,
                      width: c.w * view.scale, height: (c.h ?? CELL_H) * view.scale,
                    }}
                    title={cellLabel(c)}
                    onPointerDown={row || readOnly ? undefined : (e) => startCell('move', i, k, e)}
                  >
                    {cellLabel(c)}
                    {!row && !readOnly && <span className="pe-cell-grip" onPointerDown={(e) => startCell('size', i, k, e)} />}
                  </span>
                )))
                : r.columns.map((c, k) => (
                  <span
                    key={k}
                    className="pe-region-col"
                    style={{ left: c.dx * view.scale, width: c.w * view.scale }}
                  />
                ))}
              {!readOnly && <span className="pe-box-grip" onPointerDown={(e) => startResizeRegion(i, e)} />}
            </div>
          );
        })}

        {boxesOnPage.map(({ p, i }) => {
          const on = sel?.kind === 'placement' && sel.index === i;
          return (
            <div
              key={`p${i}`}
              className={`pe-box ${on ? 'is-on' : ''}`}
              style={boxStyle(p.x, p.y, p.w, p.h)}
              onPointerDown={(e) => startMove('placement', i, e)}
              title={labelOf(p.fieldKey)}
              data-key={p.fieldKey}
            >
              <span className="pe-box-label">{labelOf(p.fieldKey)}</span>
              {!readOnly && <span className="pe-box-grip" onPointerDown={(e) => startResize(i, e)} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export interface PlacementPropsProps {
  sel: PlacementSel;
  placements: Placement[];
  regions: Region[];
  onChange: (placements: Placement[], regions: Region[]) => void;
  onSel: (sel: PlacementSel) => void;
  labelOf: (key: string) => string;
  scopes: ScopeOption[];
  signatureKeys: string[];
  /** The same answers arranged the way the form is built: the main form, and one list per repeating container. */
  answerGroups: OptGroup[];
  scopeGroups: Record<string, OptGroup[]>;
  disabled?: boolean;
  /** The page’s height in points, so a block’s place can be typed from the TOP of the page. */
  pageHeight?: number;
}

/**
 * One selected box's or table's properties — text size, alignment, format,
 * the signature's stamp and IP, shrink-to-fit, the table's rows, columns and
 * total row — and its Remove. Lifted from the old properties rail unchanged.
 */
export function PlacementProps({
  sel, placements, regions, onChange, onSel, labelOf, scopes, signatureKeys, answerGroups, scopeGroups, disabled, pageHeight,
}: PlacementPropsProps) {
  const isSignature = (key: string) => signatureKeys.includes(key);
  const scopeOf = (scope: string) => scopes.find((s) => s.scope === scope);
  const updateP = (index: number, patch: Partial<Placement>) =>
    onChange(placements.map((p, i) => (i === index ? { ...p, ...patch } : p)), regions);
  const updateR = (index: number, patch: Partial<Region>) =>
    onChange(placements, regions.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const selP = sel?.kind === 'placement' ? placements[sel.index] : undefined;
  const selR = sel?.kind === 'region' ? regions[sel.index] : undefined;
  const selScope = selR ? scopeOf(selR.scope) : undefined;

  if (selP && sel) {
    return (
      <fieldset className="pe-props-body" disabled={disabled}>
        <div className="pe-overline">{labelOf(selP.fieldKey)} · page {selP.page}</div>
        <label className="pe-prop">
          <span>Text size</span>
          <input
            type="number" min={4} max={96} value={selP.fontSize ?? 10}
            onChange={(e) => updateP(sel.index, { fontSize: Number(e.target.value) || 10 })}
          />
        </label>
        <label className="pe-prop">
          <span>Align</span>
          <select
            value={selP.align || 'left'}
            onChange={(e) => updateP(sel.index, { align: e.target.value as Placement['align'] })}
          >
            <option value="left">Left</option>
            <option value="center">Centre</option>
            <option value="right">Right</option>
          </select>
        </label>
        <label className="pe-prop">
          <span>{isSignature(selP.fieldKey) ? 'What to draw' : 'Format'}</span>
          <select
            value={selP.format || 'text'}
            onChange={(e) => updateP(sel.index, { format: e.target.value as Placement['format'] })}
          >
            {isSignature(selP.fieldKey) ? (
              <>
                {/* One field, two things worth printing — so the same
                    signature is placed twice: the mark on the line,
                    the stamp underneath it. */}
                <option value="text">The signature itself</option>
                <option value="signedAt">When it was signed</option>
              </>
            ) : (
              <>
                <option value="text">As typed</option>
                <option value="date">Date — 09/03/2026</option>
                <option value="number">Number — 1,234</option>
                <option value="currency">Amount — 1,234.50</option>
                <option value="checkbox">Tick — X when yes</option>
              </>
            )}
          </select>
        </label>
        {isSignature(selP.fieldKey) && selP.format === 'signedAt' && (
          <label className="pe-prop pe-prop-check">
            <input
              type="checkbox" checked={!!selP.withIp}
              onChange={(e) => updateP(sel.index, { withIp: e.target.checked })}
            />
            <span>Include the signer’s IP address</span>
          </label>
        )}
        {isSignature(selP.fieldKey) && selP.format !== 'signedAt' ? (
          <div className="pe-hint">
            The signature is scaled to fit this box without distorting — a stretched
            signature looks forged. Make the box the shape of the line it sits on.
          </div>
        ) : (
          <label className="pe-prop pe-prop-check">
            <input
              type="checkbox" checked={selP.shrinkToFit !== false}
              onChange={(e) => updateP(sel.index, { shrinkToFit: e.target.checked })}
            />
            <span>Shrink long answers to fit the box</span>
          </label>
        )}
        <div className="pe-coords">
          x {Math.round(selP.x)} · y {Math.round(selP.y)} · {Math.round(selP.w)}×{Math.round(selP.h)}pt
        </div>
        <button
          type="button" className="fs-btn sm"
          onClick={() => { onChange(placements.filter((_, i) => i !== sel.index), regions); onSel(null); }}
        >
          Remove this box
        </button>
      </fieldset>
    );
  }

  /** Table or repeating block — the one choice both kinds of region share. */
  const layoutPicker = selR && sel && (
    <label className="pe-prop">
      <span>Repeats as</span>
      <select
        value={selR.layout === 'blocks' ? 'blocks' : 'rows'}
        onChange={(e) => {
          // The patch is merged over the region, so what a layout drops is named.
          if (e.target.value === 'blocks') { updateR(sel.index, { ...toBlocks(selR), total: undefined }); return; }
          const table = toTable(selR);
          // A block that only counted and numbered has nothing a table can
          // keep, and a table with no column draws nothing — and is not stored.
          const first = selScope?.fields[0]?.key;
          if (!table.columns.length && first) table.columns = [{ fieldKey: first, dx: 0, w: 180 }];
          // `layout` and `cover` are named so the patch clears them.
          updateR(sel.index, { ...table, layout: undefined, cover: undefined });
        }}
      >
        <option value="rows">A table — one line each</option>
        <option value="blocks">A repeating block — a group of answers each</option>
      </select>
    </label>
  );

  if (selR && sel && selR.layout === 'blocks') {
    const item = (selScope?.label || selR.scope).toLowerCase();
    const setCol = (k: number, patch: Partial<RegionColumn>) =>
      updateR(sel.index, { columns: selR.columns.map((x, j) => (j === k ? { ...x, ...patch } : x)) });
    const numberOf = (v: string, fallback: number) => (Number.isFinite(Number(v)) && v !== '' ? Number(v) : fallback);
    const firstField = selScope?.fields[0]?.key || '';
    return (
      <fieldset className="pe-props-body" disabled={disabled}>
        <div className="pe-overline">{selScope?.label || selR.scope} · page {selR.page}</div>
        <div className="pe-hint">
          One block per {item} on the submission: its answers placed anywhere inside the block,
          and the block repeated down the page.
        </div>
        {layoutPicker}

        {/* Where the first block sits, typed: a block has to land on a frame
            the PDF already drew, which a drag gets close to and a number gets right. */}
        <div className="pe-trio">
          <label className="pe-prop">
            <span>Left (pt)</span>
            <input
              type="number" value={selR.x} aria-label="Left edge of the block (pt)"
              onChange={(e) => updateR(sel.index, { x: numberOf(e.target.value, 0) })}
            />
          </label>
          {pageHeight !== undefined && (
            <label className="pe-prop">
              <span>Top (pt)</span>
              <input
                type="number" value={Math.round(pageHeight - selR.y - selR.rowHeight)}
                aria-label="Top of the first block, from the top of the page (pt)"
                onChange={(e) => updateR(sel.index, { y: pageHeight - numberOf(e.target.value, 0) - selR.rowHeight })}
              />
            </label>
          )}
          <label className="pe-prop">
            <span>Width (pt)</span>
            <input
              type="number" min={1} value={selR.w} aria-label="Width of the block (pt)"
              onChange={(e) => updateR(sel.index, { w: Math.max(1, Number(e.target.value) || 400) })}
            />
          </label>
        </div>

        <label className="pe-prop">
          <span>Block height (pt)</span>
          <input
            type="number" min={6} max={400} value={selR.rowHeight}
            onChange={(e) => {
              const rowHeight = Number(e.target.value) || BLOCK_H;
              // The first block's top stays where it was put.
              updateR(sel.index, { rowHeight, y: selR.y + selR.rowHeight - rowHeight });
            }}
          />
          <small className="pe-note">From the top of one block to the top of the next.</small>
        </label>
        <label className="pe-prop">
          <span>Blocks before a new page</span>
          <input
            type="number" min={1} max={500} value={selR.maxRows ?? BLOCK_ROWS}
            onChange={(e) => updateR(sel.index, { maxRows: Number(e.target.value) || BLOCK_ROWS })}
          />
          <small className="pe-note">
            More items than this and the blocks continue on a copy of this page. None is ever dropped.
          </small>
        </label>
        <label className="pe-prop pe-prop-check">
          <input
            type="checkbox" checked={!!selR.cover}
            onChange={(e) => updateR(sel.index, { cover: e.target.checked ? '#ffffff' : undefined })}
          />
          <span>Cover the blocks a page has no {item} for</span>
        </label>
        {selR.cover && (
          <label className="pe-prop">
            <span>Cover them with</span>
            <input
              type="color" value={selR.cover} aria-label="Colour that covers an unused block"
              onChange={(e) => updateR(sel.index, { cover: e.target.value })}
            />
            <small className="pe-note">
              The page’s own background colour, so a frame and labels your PDF drew for a block
              nobody filled do not print.
            </small>
          </label>
        )}

        <div className="pe-overline" style={{ marginTop: 14 }}>In each block</div>
        {selR.columns.map((c, k) => (
          <div className="pe-col" key={k}>
            <select
              aria-label="What this prints"
              value={c.index ? 'index' : c.count ? `count:${c.count}` : 'answer'}
              onChange={(e) => {
                const v = e.target.value;
                const { count: _count, index: _index, ...plain } = c;
                const next: RegionColumn = v === 'index' ? { ...plain, fieldKey: '', index: true }
                  : v.startsWith('count:') ? { ...plain, fieldKey: '', count: v.slice(6) }
                    : { ...plain, fieldKey: c.fieldKey || firstField };
                updateR(sel.index, { columns: selR.columns.map((x, j) => (j === k ? next : x)) });
              }}
            >
              <option value="answer">An answer</option>
              <option value="index">Its number — 1, 2, 3…</option>
              {(selScope?.inside || []).map((s) => (
                <option key={s} value={`count:${s}`}>How many {scopeOf(s)?.label || s}</option>
              ))}
              {/* A count stored against a container the form no longer nests here still shows what it is. */}
              {c.count && !(selScope?.inside || []).includes(c.count) && (
                <option value={`count:${c.count}`}>How many {scopeOf(c.count)?.label || c.count}</option>
              )}
            </select>
            {!c.index && !c.count && (
              <FieldPicker
                value={c.fieldKey} allowClear={false}
                groups={selScope && scopeGroups[selScope.scope]?.length ? scopeGroups[selScope.scope] : answerGroups}
                onChange={(v) => setCol(k, { fieldKey: v })}
              />
            )}
            <div className="pe-col-row pe-cell-row">
              <input
                type="number" title="Across from the block's left edge (pt)" aria-label="Across from the block's left edge (pt)" value={c.dx}
                onChange={(e) => setCol(k, { dx: numberOf(e.target.value, 0) })}
              />
              <input
                type="number" title="Down from the block's top (pt)" aria-label="Down from the block's top (pt)" value={c.dy ?? 0}
                onChange={(e) => setCol(k, { dy: numberOf(e.target.value, 0) })}
              />
              <input
                type="number" title="Width (pt)" aria-label="Width (pt)" value={c.w}
                onChange={(e) => setCol(k, { w: Number(e.target.value) || 80 })}
              />
              <input
                type="number" title="Height (pt) — a taller box takes more lines" aria-label="Height (pt)" value={c.h ?? CELL_H}
                onChange={(e) => setCol(k, { h: Number(e.target.value) || CELL_H })}
              />
              <button
                type="button" className="pe-col-x" title="Remove this answer" aria-label="Remove this answer"
                onClick={() => updateR(sel.index, { columns: selR.columns.filter((_, j) => j !== k) })}
              >
                ×
              </button>
            </div>
            <div className="pe-col-row pe-cell-style">
              <input
                type="number" min={4} max={96} title="Text size (pt)" aria-label="Text size (pt)" value={c.fontSize ?? 10}
                onChange={(e) => setCol(k, { fontSize: Number(e.target.value) || 10 })}
              />
              <select
                value={c.align || 'left'} aria-label="Alignment"
                onChange={(e) => setCol(k, { align: e.target.value as RegionColumn['align'] })}
              >
                <option value="left">L</option>
                <option value="center">C</option>
                <option value="right">R</option>
              </select>
              {!c.index && !c.count && (
                <select
                  value={c.format || 'text'} aria-label="Format"
                  onChange={(e) => setCol(k, { format: e.target.value as RegionColumn['format'] })}
                >
                  <option value="text">As typed</option>
                  <option value="date">Date</option>
                  <option value="number">Number</option>
                  <option value="currency">Amount</option>
                  <option value="checkbox">Tick</option>
                </select>
              )}
            </div>
          </div>
        ))}
        <button
          type="button" className="fs-btn sm"
          disabled={!selScope?.fields.length && !selScope?.inside?.length}
          onClick={() => {
            const next = selScope?.fields[selR.columns.filter((c) => c.fieldKey).length] || selScope?.fields[0];
            // Under the lowest box there is, so a new one never lands on another.
            const below = selR.columns.reduce((y, c) => Math.max(y, (c.dy ?? 0) + (c.h ?? CELL_H) + 4), 0);
            updateR(sel.index, {
              columns: [...selR.columns, {
                ...(next ? { fieldKey: next.key } : { fieldKey: '', index: true }),
                dx: 0, dy: below, w: 180, h: CELL_H, fontSize: 10,
              }],
            });
          }}
        >
          + Add an answer
        </button>
        <small className="pe-note">
          Drag an answer’s box inside the first block on the page to move it, and its corner to size it.
          A long answer wraps onto as many lines as its box is tall.
        </small>

        <div className="pe-coords">
          x {Math.round(selR.x)} · y {Math.round(selR.y)} · {Math.round(selR.w)}pt wide
        </div>
        <button
          type="button" className="fs-btn sm"
          onClick={() => { onChange(placements, regions.filter((_, i) => i !== sel.index)); onSel(null); }}
        >
          Remove this block
        </button>
      </fieldset>
    );
  }

  if (selR && sel) {
    return (
      <fieldset className="pe-props-body" disabled={disabled}>
        <div className="pe-overline">{selScope?.label || selR.scope} · page {selR.page}</div>
        <div className="pe-hint">
          One row per {(selScope?.label || selR.scope).toLowerCase()} on the submission.
        </div>
        {layoutPicker}

        <label className="pe-prop">
          <span>Row height (pt)</span>
          <input
            type="number" min={6} max={400} value={selR.rowHeight}
            onChange={(e) => updateR(sel.index, { rowHeight: Number(e.target.value) || 18 })}
          />
        </label>
        <label className="pe-prop">
          <span>Rows before a new page</span>
          <input
            type="number" min={1} max={500} value={selR.maxRows ?? 8}
            onChange={(e) => updateR(sel.index, { maxRows: Number(e.target.value) || 8 })}
          />
          <small className="pe-note">
            More items than this and the table continues on a copy of this page. Rows are
            never dropped.
          </small>
        </label>

        <div className="pe-overline" style={{ marginTop: 14 }}>Columns</div>
        {selR.columns.map((c, k) => (
          <div className="pe-col" key={k}>
            <FieldPicker
              value={c.fieldKey} allowClear={false}
              groups={selScope && scopeGroups[selScope.scope]?.length ? scopeGroups[selScope.scope] : answerGroups}
              onChange={(v) => updateR(sel.index, {
                columns: selR.columns.map((x, j) => (j === k ? { ...x, fieldKey: v } : x)),
              })}
            />
            <div className="pe-col-row">
              <input
                type="number" title="Offset from the table's left edge (pt)" aria-label="Offset from the table's left edge (pt)" value={c.dx}
                onChange={(e) => updateR(sel.index, {
                  columns: selR.columns.map((x, j) => (j === k ? { ...x, dx: Number(e.target.value) || 0 } : x)),
                })}
              />
              <input
                type="number" title="Column width (pt)" aria-label="Column width (pt)" value={c.w}
                onChange={(e) => updateR(sel.index, {
                  columns: selR.columns.map((x, j) => (j === k ? { ...x, w: Number(e.target.value) || 80 } : x)),
                })}
              />
              <select
                value={c.align || 'left'} aria-label="Column alignment"
                onChange={(e) => updateR(sel.index, {
                  columns: selR.columns.map((x, j) => (j === k
                    ? { ...x, align: e.target.value as 'left' | 'center' | 'right' } : x)),
                })}
              >
                <option value="left">L</option>
                <option value="center">C</option>
                <option value="right">R</option>
              </select>
              <button
                type="button" className="pe-col-x" title="Remove this column" aria-label="Remove this column"
                onClick={() => updateR(sel.index, { columns: selR.columns.filter((_, j) => j !== k) })}
              >
                ×
              </button>
            </div>
            <select
              value={c.format || 'text'} aria-label="Column format"
              onChange={(e) => updateR(sel.index, {
                columns: selR.columns.map((x, j) => (j === k
                  ? { ...x, format: e.target.value as Region['columns'][number]['format'] } : x)),
              })}
            >
              <option value="text">As typed</option>
              <option value="date">Date</option>
              <option value="number">Number</option>
              <option value="currency">Amount</option>
              <option value="checkbox">Tick</option>
            </select>
          </div>
        ))}
        <button
          type="button" className="fs-btn sm"
          disabled={!selScope?.fields.length}
          onClick={() => {
            const next = selScope?.fields[selR.columns.length] || selScope?.fields[0];
            const last = selR.columns[selR.columns.length - 1];
            updateR(sel.index, {
              columns: [...selR.columns, {
                fieldKey: next?.key || '',
                dx: last ? last.dx + last.w + 10 : 0,
                w: 80,
              }],
            });
          }}
        >
          + Add a column
        </button>

        <div className="pe-overline" style={{ marginTop: 14 }}>Total row</div>
        <label className="pe-prop">
          <span>Totals which column</span>
          <select
            value={selR.total?.column || ''}
            onChange={(e) => updateR(sel.index, {
              total: e.target.value
                ? {
                  column: e.target.value,
                  op: selR.total?.op || 'sum',
                  dx: selR.total?.dx ?? (selR.columns[selR.columns.length - 1]?.dx || 0),
                  label: selR.total?.label,
                }
                : undefined,
            })}
          >
            <option value="">No total</option>
            {selR.columns.map((c, k) => (
              <option key={k} value={c.fieldKey}>{labelOf(c.fieldKey)}</option>
            ))}
          </select>
        </label>
        {selR.total && (
          <>
            <label className="pe-prop">
              <span>Add up or count</span>
              <select
                value={selR.total.op}
                onChange={(e) => updateR(sel.index, {
                  total: { ...selR.total!, op: e.target.value as 'sum' | 'count' },
                })}
              >
                <option value="sum">Add the values up</option>
                <option value="count">Count the rows</option>
              </select>
            </label>
            <label className="pe-prop">
              <span>Label (optional)</span>
              <input
                value={selR.total.label || ''} placeholder="Total monthly fees:"
                onChange={(e) => updateR(sel.index, {
                  total: { ...selR.total!, label: e.target.value },
                })}
              />
            </label>
            <small className="pe-note">
              Counted over every row, including any that continued onto another page.
            </small>
          </>
        )}

        <div className="pe-coords">
          x {Math.round(selR.x)} · y {Math.round(selR.y)} · {Math.round(selR.w)}pt wide
        </div>
        <button
          type="button" className="fs-btn sm"
          onClick={() => { onChange(placements, regions.filter((_, i) => i !== sel.index)); onSel(null); }}
        >
          Remove this table
        </button>
      </fieldset>
    );
  }

  return null;
}
